// src/hooks/prefect.ts
import { useState, useEffect } from "react";

// -----------------------------
// Interfaces for Prefect Models
// -----------------------------

export interface PrefectPipelineData {
    entry_id: number;
    area: string;
    timestamp: string;
    status: boolean;
    runtime: string;
    prefect_url: string;
    created_at: string;
}

export interface PrefectEmailData {
    entry_id: number;
    area: string;
    organization: number;
    email_timestamp: string;
    status: boolean;
    empty_email: boolean;
    content: string;
    created_at: string;
}

export interface PrefectKeyData {
    entry_id: number;
    area: string;
    organization: number;
    last_used: string;
    active: boolean;
    active_since: string;
    header_key: string;
    created_at: string;
}

// -----------------------------
// Hook Implementation
// -----------------------------
export function usePrefectHistory(pollInterval = 30000) {
    const [pipelineHistory, setPipelineHistory] = useState<PrefectPipelineData[]>([]);
    const [emailHistory, setEmailHistory] = useState<PrefectEmailData[]>([]);
    const [keyHistory, setKeyHistory] = useState<PrefectKeyData[]>([]);

    const fetchPipeline = async () => {
        try {
            const res = await fetch("/api/aiteam/pipeline/history/", {
                credentials: "same-origin",
            });
            if (!res.ok) throw new Error(`Pipeline HTTP ${res.status}`);
            setPipelineHistory(await res.json());
        } catch (err) {
            console.error("Error fetching Prefect Pipeline History:", err);
            setPipelineHistory([]);
        }
    };

    const fetchEmail = async () => {
        try {
            const res = await fetch("/api/aiteam/email/history/", {
                credentials: "same-origin",
            });
            if (!res.ok) throw new Error(`Email HTTP ${res.status}`);
            setEmailHistory(await res.json());
        } catch (err) {
            console.error("Error fetching Prefect Email History:", err);
            setEmailHistory([]);
        }
    };

    const fetchKey = async () => {
        try {
            const res = await fetch("/api/aiteam/key/history/", {
                credentials: "same-origin",
            });
            if (!res.ok) throw new Error(`Key HTTP ${res.status}`);
            setKeyHistory(await res.json());
        } catch (err) {
            console.error("Error fetching Prefect Key History:", err);
            setKeyHistory([]);
        }
    };

    // Fetch all on load + poll
    useEffect(() => {
        fetchPipeline();
        fetchEmail();
        fetchKey();

        const interval = setInterval(() => {
            fetchPipeline();
            fetchEmail();
            fetchKey();
        }, pollInterval);

        return () => clearInterval(interval);
    }, []);

    return {
        pipelineHistory,
        emailHistory,
        keyHistory,
        refreshAll: () => {
            fetchPipeline();
            fetchEmail();
            fetchKey();
        },
    };
}
